import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { NotificationPermissionBanner, NotificationBell } from './NotificationPermission';
import { SupportButton } from './SupportButton';
import { 
  Car, 
  Truck, 
  Key, 
  Wallet, 
  User, 
  LogOut, 
  Home,
  LayoutDashboard,
  Menu,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const NavItem = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
      active 
        ? 'bg-[#00BFA6]/10 text-[#00BFA6]' 
        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
    }`}
    data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
  >
    <Icon className="w-5 h-5" />
    <span className="font-medium">{label}</span>
  </Link>
);

const MobileNavItem = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    className={`flex flex-col items-center gap-1 py-2 px-3 ${
      active ? 'text-[#00BFA6]' : 'text-slate-500'
    }`}
    data-testid={`mobile-nav-${label.toLowerCase()}`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-xs">{label}</span>
  </Link>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/home', icon: Home, label: 'Inicio' },
    { to: '/ride', icon: Car, label: 'Viajes' },
    { to: '/rental', icon: Key, label: 'Rentar' },
    { to: '/delivery', icon: Truck, label: 'Envíos' },
    { to: '/wallet', icon: Wallet, label: 'Wallet' },
  ];

  const roleNavItems = user?.role === 'driver' || user?.role === 'admin' 
    ? [{ to: '/driver', icon: LayoutDashboard, label: 'Conductor' }]
    : [];

  const adminNavItems = user?.role === 'admin'
    ? [{ to: '/admin', icon: LayoutDashboard, label: 'Admin' }]
    : [];

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 flex-col sidebar-light z-40">
        {/* Logo */}
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center justify-center" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
              alt="Mercapoli Move"
              className="h-10"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              active={location.pathname === item.to}
            />
          ))}
          
          {roleNavItems.length > 0 && (
            <>
              <div className="pt-4 pb-2">
                <span className="px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Conductor
                </span>
              </div>
              {roleNavItems.map((item) => (
                <NavItem
                  key={item.to}
                  {...item}
                  active={location.pathname === item.to}
                />
              ))}
            </>
          )}

          {adminNavItems.length > 0 && (
            <>
              <div className="pt-4 pb-2">
                <span className="px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Administración
                </span>
              </div>
              {adminNavItems.map((item) => (
                <NavItem
                  key={item.to}
                  {...item}
                  active={location.pathname === item.to}
                />
              ))}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          <Link
            to="/profile"
            className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-100 transition-colors"
            data-testid="profile-link"
          >
            <div className="w-10 h-10 rounded-full bg-[#00BFA6]/10 flex items-center justify-center">
              <User className="w-5 h-5 text-[#00BFA6]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </Link>
          <Button
            variant="ghost"
            className="w-full mt-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-xl border-b border-slate-200 z-40 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2" data-testid="mobile-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_aa9a9291-9586-4ce8-b3a3-ae4b4447418a/artifacts/e5dffy9m_Move_Icono_Trans.png" 
            alt="Mercapoli Move"
            className="w-8 h-8"
          />
          <span className="font-bold text-slate-800 font-['Outfit'] text-sm">MERCAPOLI MOVE</span>
        </Link>

        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-slate-700" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-white border-slate-200 w-72">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-6">
                <span className="font-bold text-slate-800">Menú</span>
              </div>
              
              <nav className="flex-1 space-y-2">
                {[...navItems, ...roleNavItems, ...adminNavItems].map((item) => (
                  <NavItem
                    key={item.to}
                    {...item}
                    active={location.pathname === item.to}
                  />
                ))}
              </nav>

              <div className="pt-4 border-t border-slate-200">
                <Link
                  to="/profile"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="w-10 h-10 rounded-full bg-[#00BFA6]/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#00BFA6]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{user?.full_name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                </Link>
                <Button
                  variant="ghost"
                  className="w-full mt-2 text-slate-600 hover:text-slate-900"
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesión
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 pb-20 lg:pb-0 min-h-screen">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 mobile-nav z-40">
        <div className="flex items-center justify-around py-2">
          {navItems.slice(0, 5).map((item) => (
            <MobileNavItem
              key={item.to}
              {...item}
              active={location.pathname === item.to}
            />
          ))}
        </div>
      </nav>

      {/* Notification Permission Banner */}
      <NotificationPermissionBanner />

      {/* Floating Support Button */}
      <SupportButton variant="floating" />
    </div>
  );
};

export default Layout;
